import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BellRing, LoaderCircle, Trash2, X } from 'lucide-react';
import { cn } from '../lib/cn';
import './add-profile-dialog-stitch.css';
import './confirm-action-dialog-stitch.css';

interface ConfirmActionDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  eyebrow?: string;
  cancelLabel?: string;
  showCancel?: boolean;
  tone?: 'accent' | 'danger';
  busy?: boolean;
  headerAside?: ReactNode;
  extraContent?: ReactNode;
  hideShortcutHint?: boolean;
  footerAccessory?: ReactNode;
  footerAccessoryPlacement?: 'meta' | 'actions-leading';
  layout?: 'default' | 'slim' | 'workspace' | 'transfer';
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

const modalClassName =
  'fixed left-1/2 top-1/2 z-[430] flex max-h-[80vh] w-[min(96vw,1180px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(30,30,30,0.7)_0%,rgba(20,20,20,0.8)_100%)] text-gray-100 shadow-[0_25px_60px_rgba(0,0,0,0.45)] backdrop-blur-[40px]';

const cardClassName =
  'rounded-[20px] border border-white/10 bg-[rgba(45,45,45,0.6)] p-6 shadow-[0_12px_28px_rgba(0,0,0,0.22)] backdrop-blur-[30px] transition duration-200 hover:-translate-y-[2px] hover:shadow-[0_18px_36px_rgba(0,0,0,0.3)]';

export function ConfirmActionDialog({
  open,
  title,
  description,
  confirmLabel,
  eyebrow = '确认操作',
  cancelLabel = '取消',
  showCancel = true,
  tone = 'accent',
  busy = false,
  headerAside,
  extraContent,
  hideShortcutHint = false,
  footerAccessory,
  footerAccessoryPlacement = 'meta',
  layout = 'default',
  onConfirm,
  onOpenChange,
}: ConfirmActionDialogProps) {
  const AccentIcon = tone === 'danger' ? Trash2 : BellRing;
  const useWorkspaceSwitchFrame = layout === 'workspace' || layout === 'transfer';
  const useCompactWorkspaceFrame = layout === 'workspace';
  const useSlimDialog = layout === 'slim';
  const isProfileTransferDialog = layout === 'transfer';
  const statusText = busy
    ? '处理中'
    : eyebrow === '切换完成'
      ? '已完成'
      : eyebrow === '当前空间'
        ? '无需切换'
        : '等待确认';
  const actionTypeText =
    tone === 'danger'
      ? '危险操作'
      : eyebrow === '空间卡片传送' || eyebrow === '云端备份'
        ? '卡片导入'
        : eyebrow === '覆盖同命名空间'
          ? '覆盖确认'
          : eyebrow === '切换完成'
            ? '结果提示'
            : '切换提示';
  const shortcutText = showCancel ? '⌘ + Enter' : 'Enter';
  const overviewSummary =
    tone === 'danger'
      ? '确认后会按当前勾选执行删除或覆盖操作，请先确认目标是否正确。'
      : eyebrow === '空间卡片传送' || eyebrow === '云端备份'
        ? '确认后会把远程传来的空间卡片接入本地卡片池，后续即可继续参与切换与推荐逻辑。'
        : eyebrow === '覆盖同命名空间'
          ? '确认后会直接覆盖同命名空间的现有配置，不会额外新增一张卡片。'
          : '确认后会继续执行当前切换或授权相关动作，并刷新本地展示状态。';
  const overviewCards = [
    { label: '当前提示', value: eyebrow },
    { label: '执行状态', value: statusText, dot: !busy && eyebrow !== '切换完成' },
    { label: '操作类型', value: actionTypeText },
    { label: '快捷确认', value: shortcutText },
  ];

  useEffect(() => {
    if (!useSlimDialog || !open || typeof window === 'undefined') {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onOpenChange, open, useSlimDialog]);

  if (useWorkspaceSwitchFrame) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay
            className={cn(
              'confirm-action-dialog-overlay',
              isProfileTransferDialog && 'confirm-action-dialog-overlay--transfer',
            )}
          />
          <Dialog.Content asChild>
            <div className="confirm-action-dialog-shell">
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -left-28 top-16 h-72 w-72 rounded-full bg-white/[0.03] blur-3xl" />
                <div className="absolute right-[-120px] top-24 h-80 w-80 rounded-full bg-blue-500/[0.07] blur-3xl" />
                <div className="absolute bottom-[-140px] left-1/2 h-96 w-[30rem] -translate-x-1/2 rounded-full bg-blue-400/[0.04] blur-3xl" />
              </div>

              <div className="relative flex min-h-screen items-center justify-center px-5 py-8 lg:px-10">
                <div
                  id="confirm-action-modal"
                  className={cn(
                    'glass-panel flex h-[78vh] w-full max-w-[1220px] flex-col overflow-hidden rounded-[28px] border border-white/10 shadow-[0_28px_120px_rgba(0,0,0,0.5)]',
                    useCompactWorkspaceFrame && 'confirm-action-modal--workspace',
                    isProfileTransferDialog && 'confirm-action-modal--transfer',
                  )}
                >
                  <header className="border-b border-white/10 px-10 py-7">
                    <div className="confirm-action-dialog-header-body">
                      <div className="confirm-action-dialog-header-main">
                        <Dialog.Title asChild>
                          <h1 className="text-[2.25rem] font-semibold tracking-[-0.04em] text-white">
                            {title}
                          </h1>
                        </Dialog.Title>
                        <Dialog.Description asChild>
                          <p className="mt-2 text-base text-gray-400">{description}</p>
                        </Dialog.Description>
                      </div>

                      <div className="confirm-action-dialog-header-side">
                        {headerAside ? (
                          <div className="confirm-action-dialog-header-aside">{headerAside}</div>
                        ) : null}

                        <Dialog.Close asChild>
                          <button
                            id="confirm-action-close-button"
                            type="button"
                            aria-label="关闭弹窗"
                            className="rounded-full p-3 text-white/90 transition hover:bg-white/10 hover:text-white"
                          >
                            <X className="h-7 w-7" strokeWidth={2.1} />
                          </button>
                        </Dialog.Close>
                      </div>
                    </div>
                  </header>

                  <main
                    className={cn(
                      'soft-scrollbar flex-1 overflow-y-auto px-10 py-8',
                      useCompactWorkspaceFrame && 'confirm-action-dialog-main--workspace',
                      isProfileTransferDialog && 'confirm-action-dialog-main--transfer',
                    )}
                  >
                    {isProfileTransferDialog ? (
                      <div className="confirm-action-dialog-grid confirm-action-dialog-grid--transfer">
                        <section className="confirm-action-dialog-grid__content confirm-action-dialog-grid__content--transfer">
                          <div className="soft-card confirm-action-workspace-body confirm-action-workspace-body--transfer">
                            <div className="confirm-action-detail-body confirm-action-detail-body--transfer">
                              {extraContent}
                            </div>
                          </div>
                        </section>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          'confirm-action-dialog-grid',
                          useCompactWorkspaceFrame && 'confirm-action-dialog-grid--workspace',
                        )}
                      >
                        <section className="confirm-action-dialog-grid__overview">
                          <div
                            className={cn(
                              'soft-card confirm-action-overview-card',
                              useCompactWorkspaceFrame && 'confirm-action-overview-card--workspace',
                            )}
                          >
                            <div className="confirm-action-overview-card__head">
                              <span
                                className={cn(
                                  'confirm-action-overview-card__icon',
                                  tone === 'danger' && 'confirm-action-overview-card__icon--danger',
                                )}
                              >
                                <AccentIcon className="h-5 w-5" strokeWidth={1.9} />
                              </span>
                              <div>
                                <div className="confirm-action-overview-card__eyebrow">
                                  {eyebrow}
                                </div>
                                <div className="confirm-action-overview-card__action">
                                  {confirmLabel}
                                </div>
                              </div>
                            </div>

                            {!useCompactWorkspaceFrame ? (
                              <div className="confirm-action-overview-card__summary">
                                {overviewSummary}
                              </div>
                            ) : null}

                            <div className="confirm-action-overview-card__metrics">
                              {overviewCards.map((item) => (
                                <div key={item.label} className="status-tile">
                                  <p className="tile-label">{item.label}</p>
                                  <div className="mt-3 flex items-center gap-2.5">
                                    {item.dot ? <span className="status-dot ready" /> : null}
                                    <p className="text-[1.02rem] font-semibold text-gray-100">
                                      {item.value}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </section>

                        <section
                          className={cn(
                            'confirm-action-dialog-grid__content',
                            useCompactWorkspaceFrame && 'confirm-action-dialog-grid__content--workspace',
                          )}
                        >
                          <div className="soft-card confirm-action-workspace-body">
                            <div className="confirm-action-copy-card__highlight">
                              <div className="confirm-action-copy-card__meta">
                                <span className="confirm-action-copy-card__meta-label">
                                  当前操作
                                </span>
                                <span className="confirm-action-copy-card__meta-value">
                                  {confirmLabel}
                                </span>
                              </div>
                              <div className="confirm-action-copy-card__meta">
                                <span className="confirm-action-copy-card__meta-label">
                                  当前状态
                                </span>
                                <span className="confirm-action-copy-card__meta-value">
                                  {statusText}
                                </span>
                              </div>
                            </div>

                            {!useCompactWorkspaceFrame ? (
                              <p className="confirm-action-copy-card__description">{description}</p>
                            ) : null}

                            <div
                              className={cn(
                                'confirm-action-detail-body',
                                useCompactWorkspaceFrame &&
                                  'confirm-action-detail-body--workspace',
                              )}
                            >
                              {extraContent ? (
                                extraContent
                              ) : (
                                <div className="grid gap-4 md:grid-cols-3">
                                  <div>
                                    <div className="text-[10px] uppercase">确认方式</div>
                                    <div className="mt-2 text-sm font-semibold text-white">
                                      {confirmLabel}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] uppercase">次要操作</div>
                                    <div className="mt-2 text-sm font-semibold text-white">
                                      {showCancel ? cancelLabel : '无'}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-[10px] uppercase">当前状态</div>
                                    <div className="mt-2 text-sm font-semibold text-white">
                                      {statusText}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </section>
                      </div>
                    )}
                  </main>

                  <footer className="confirm-action-dialog-footer flex flex-col gap-5 border-t border-white/10 bg-black/20 px-10 py-6 md:flex-row md:items-center md:justify-between">
                    <div className="confirm-action-dialog-footer__meta">
                      {!hideShortcutHint ? (
                        <div className="confirm-action-dialog-footer__shortcut flex flex-wrap items-center gap-3 text-sm text-gray-500">
                          <div className="confirm-action-shortcut">
                            <kbd className="confirm-action-shortcut__key">⌘</kbd>
                            <span className="confirm-action-shortcut__plus">+</span>
                            <kbd className="confirm-action-shortcut__key">Enter</kbd>
                            <span className="confirm-action-shortcut__label">快速确认</span>
                          </div>
                        </div>
                      ) : null}
                      {footerAccessory && footerAccessoryPlacement === 'meta' ? (
                        <div className="confirm-action-dialog-footer__accessory">
                          {footerAccessory}
                        </div>
                      ) : null}
                    </div>

                    <div className="confirm-action-dialog-footer__actions">
                      {footerAccessory && footerAccessoryPlacement === 'actions-leading' ? (
                        <div className="confirm-action-dialog-footer__accessory confirm-action-dialog-footer__accessory--actions">
                          {footerAccessory}
                        </div>
                      ) : null}
                      {showCancel ? (
                        <Dialog.Close asChild>
                          <button
                            id="confirm-action-cancel-button"
                            type="button"
                            className="confirm-action-dialog-footer__button confirm-action-dialog-footer__button--secondary secondary-button px-7 py-3 text-[15px] font-medium text-gray-200"
                          >
                            {cancelLabel}
                          </button>
                        </Dialog.Close>
                      ) : null}

                      <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        className="confirm-action-dialog-footer__button confirm-action-dialog-footer__button--primary primary-button inline-flex items-center justify-center gap-2 px-7 py-3 text-[15px] font-semibold"
                      >
                        {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                        {confirmLabel}
                      </button>
                    </div>
                  </footer>
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  if (useSlimDialog) {
    if (!open || typeof document === 'undefined') {
      return null;
    }

    return createPortal(
      <div
        className="px-4"
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 960,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
        }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            onOpenChange(false);
          }
        }}
      >
        <div
          className="bg-[rgba(3,6,12,0.52)] backdrop-blur-[14px]"
          style={{ position: 'absolute', inset: 0 }}
        />
        <div
          className="overflow-hidden rounded-[16px] border border-white/12 bg-[linear-gradient(180deg,rgba(22,27,36,0.96)_0%,rgba(16,20,28,0.94)_100%)] text-gray-100 shadow-[0_28px_72px_rgba(0,0,0,0.52)] backdrop-blur-[28px]"
          style={{
            position: 'relative',
            zIndex: 1,
            width: 'min(92vw, 520px)',
          }}
        >
          <button
            type="button"
            aria-label="关闭弹窗"
            className="text-white/80 transition hover:text-white"
            style={{ position: 'absolute', top: 14, right: 14 }}
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>

          <div className="px-8 pb-5 pt-8 text-center">
            <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-white">{title}</h2>
            {description ? (
              <p className="mx-auto mt-3 max-w-[420px] text-sm leading-6 text-gray-400">
                {description}
              </p>
            ) : null}
            {extraContent ? (
              <div className="mt-6 flex justify-center rounded-[12px] border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-gray-200">
                {extraContent}
              </div>
            ) : null}
            {footerAccessory ? <div className="mt-4 flex justify-center">{footerAccessory}</div> : null}
          </div>

          <footer className="border-t border-white/10 px-6 pb-5 pt-4">
            <div className="flex items-center justify-center gap-5">
              {showCancel ? (
                <button
                  type="button"
                  className="secondary-button inline-flex min-h-[46px] min-w-[132px] items-center justify-center px-6 py-3 text-sm font-medium text-gray-200"
                  onClick={() => onOpenChange(false)}
                >
                  {cancelLabel}
                </button>
              ) : null}
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy}
                className="primary-button inline-flex min-h-[46px] min-w-[132px] items-center justify-center gap-2 px-6 py-3 text-sm font-semibold"
              >
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                {confirmLabel}
              </button>
            </div>
          </footer>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[420] bg-[rgba(3,6,12,0.52)] backdrop-blur-[14px]" />
        <Dialog.Content className={modalClassName}>
          <header className="flex items-center justify-between border-b border-white/10 px-8 py-6">
            <div>
              <Dialog.Title className="text-[24px] font-semibold tracking-tight text-white">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-sm text-gray-400">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>

            <Dialog.Close
              aria-label="Close"
              className="rounded-full p-2 transition-colors duration-200 hover:bg-white/10"
            >
              <X className="h-6 w-6" />
            </Dialog.Close>
          </header>

          <main className="flex-grow overflow-y-auto px-8 py-8">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <section className="space-y-6 lg:col-span-5">
                <div className={cardClassName}>
                  <div className="mb-6 flex items-center gap-3">
                    <span
                      className={cn(
                        'inline-flex h-11 w-11 items-center justify-center rounded-[16px] border',
                        tone === 'danger'
                          ? 'border-red-400/25 bg-red-400/12 text-red-200'
                          : 'border-[#007AFF]/25 bg-[#007AFF]/12 text-[#88c0ff]',
                      )}
                    >
                      <AccentIcon className="h-5 w-5" strokeWidth={1.9} />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-gray-500">
                        {eyebrow}
                      </div>
                      <div className="mt-1 text-lg font-semibold text-white">{confirmLabel}</div>
                    </div>
                  </div>
                  <p className="text-sm leading-7 text-gray-300">{overviewSummary}</p>
                </div>
              </section>

              <section className="space-y-6 lg:col-span-7">
                {extraContent ? <div className={cardClassName}>{extraContent}</div> : null}
              </section>
            </div>
          </main>

          <footer className="flex items-center justify-end gap-4 border-t border-white/10 px-8 py-6">
            {showCancel ? (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="secondary-button inline-flex min-h-[48px] min-w-[120px] items-center justify-center px-6 py-3 text-sm font-medium text-gray-200"
                >
                  {cancelLabel}
                </button>
              </Dialog.Close>
            ) : null}
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="primary-button inline-flex min-h-[48px] min-w-[132px] items-center justify-center gap-2 px-6 py-3 text-sm font-semibold"
            >
              {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
              {confirmLabel}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
